import React, { useMemo, useRef, useEffect } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Goal, GoalStatus } from '../../types/Goal';
import { Relationship } from '../../types/Relationship';
import './GoalManagement.css';

interface GoalMapGraphProps {
  goals: Goal[];
  relationships: Relationship[];
  onGoalClick: (goalId: string) => void;
}

interface GraphNode {
  id: string;
  title: string;
  status: GoalStatus;
  progress: number;
  fx?: number;
  fy?: number;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
  relationship_type?: string;
}

const GoalMapGraph: React.FC<GoalMapGraphProps> = ({
  goals,
  relationships,
  onGoalClick
}) => {
  const graphRef = useRef<any>();
  const hasInitializedRef = useRef(false);

  // Get status color for goal nodes
  const getStatusColor = (status: GoalStatus): string => {
    switch (status) {
      case GoalStatus.NOT_STARTED: return '#6c757d';
      case GoalStatus.IN_PROGRESS: return '#007bff';
      case GoalStatus.COMPLETED: return '#28a745';
      case GoalStatus.ON_HOLD: return '#ffc107';
      case GoalStatus.CANCELLED: return '#dc3545';
      default: return '#6c757d';
    }
  };

  // Transform data into graph format
  const graphData = useMemo(() => {
    const nodes: GraphNode[] = goals.map(goal => ({
      id: String(goal.id),
      title: goal.title,
      status: goal.status,
      progress: goal.progress
    }));

    const links: GraphLink[] = relationships
      .filter(rel => {
        // Only include links where both source and target goals exist in the current goals list
        const sourceExists = goals.some(g => String(g.id) === String(rel.from_object_id));
        const targetExists = goals.some(g => String(g.id) === String(rel.to_object_id));
        return sourceExists && targetExists;
      })
      .map(rel => ({
        source: String(rel.from_object_id),
        target: String(rel.to_object_id),
        relationship_type: rel.relationship_type
      }));

    // Initialize nodes in a circular layout if there are nodes
    if (nodes.length > 0) {
      const radius = Math.min(200, Math.max(100, nodes.length * 20));
      const angleStep = (Math.PI * 2) / nodes.length;
      
      nodes.forEach((node, index) => {
        const angle = index * angleStep;
        node.x = Math.cos(angle) * radius;
        node.y = Math.sin(angle) * radius;
      });
    }

    return { nodes, links };
  }, [goals, relationships]);

  // Configure force simulation
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0) {
      const graph = graphRef.current;
      
      const linkForce = graph.d3Force('link');
      if (linkForce) {
        linkForce.distance(120);
        linkForce.strength(0.5);
      }
      
      const chargeForce = graph.d3Force('charge');
      if (chargeForce) {
        chargeForce.strength(-300);
      }
      
      const centerForce = graph.d3Force('center');
      if (centerForce) {
        centerForce.strength(0.05);
      }
      
      graph.d3ReheatSimulation();
    }
  }, [graphData.nodes.length, graphData.links.length]);

  // Center view after initial layout
  useEffect(() => {
    if (graphRef.current && graphData.nodes.length > 0 && !hasInitializedRef.current) {
      const graph = graphRef.current;
      
      const timer = setTimeout(() => {
        graph.zoomToFit(400, 50);
        hasInitializedRef.current = true;
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [graphData.nodes.length]);

  // Handle node click
  const handleNodeClick = (node: GraphNode) => {
    onGoalClick(node.id);
  };

  if (goals.length === 0) {
    return (
      <div className="goal-map-graph-empty">
        <p>No goals to display. Create goals to see them in the graph view.</p>
      </div>
    );
  }

  return (
    <div className="goal-map-graph-container">
      <ForceGraph2D
        ref={graphRef}
        graphData={graphData}
        nodeId="id"
        linkDirectionalArrowLength={6}
        linkDirectionalArrowRelPos={1}
        linkDirectionalArrowColor="#666666"
        onNodeClick={handleNodeClick}
        cooldownTicks={150}
        onEngineStop={() => {
          if (graphRef.current && graphData.nodes.length > 0 && !hasInitializedRef.current) {
            setTimeout(() => {
              if (graphRef.current) {
                graphRef.current.zoomToFit(400, 50);
                hasInitializedRef.current = true;
              }
            }, 100);
          }
        }}
        nodeCanvasObject={(node: GraphNode, ctx: CanvasRenderingContext2D, globalScale: number) => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;
          
          const label = node.title;
          const fontSize = Math.max(12 / globalScale, 8);
          const radius = 12;
          const statusColor = getStatusColor(node.status);
          
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
          ctx.strokeStyle = statusColor;
          ctx.lineWidth = 2;
          ctx.stroke();
          
          ctx.font = `${fontSize}px 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Source Code Pro', monospace`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.fillStyle = '#333333';
          ctx.fillText(label, node.x, node.y + radius + 5);
        }}
        nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') return;

          const pointerAreaRadius = 16;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, pointerAreaRadius, 0, 2 * Math.PI, false);
          ctx.fill();
        }}
        linkCanvasObject={(link: GraphLink, ctx: CanvasRenderingContext2D, globalScale: number) => {
          const source = link.source as GraphNode;
          const target = link.target as GraphNode;
          
          if (typeof source.x !== 'number' || typeof source.y !== 'number' || typeof target.x !== 'number' || typeof target.y !== 'number') return;
          
          const nodeRadius = 12;
          const dx = target.x - source.x;
          const dy = target.y - source.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const unitX = dx / distance;
          const unitY = dy / distance;
          
          const arrowLength = 8;
          const arrowX = target.x - unitX * nodeRadius;
          const arrowY = target.y - unitY * nodeRadius;
          
          ctx.beginPath();
          ctx.moveTo(source.x, source.y);
          ctx.lineTo(arrowX, arrowY);
          ctx.strokeStyle = '#e0e0e0';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          const angle = Math.atan2(dy, dx);
          ctx.beginPath();
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(
            arrowX - arrowLength * Math.cos(angle - Math.PI / 6),
            arrowY - arrowLength * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            arrowX - arrowLength * Math.cos(angle + Math.PI / 6),
            arrowY - arrowLength * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fillStyle = '#666666';
          ctx.fill();
          
          if (link.relationship_type) {
            const midX = (source.x + target.x) / 2;
            const midY = (source.y + target.y) / 2;
            const fontSize = Math.max(12 / globalScale, 8);
            ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#666666';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 4;
            const label = link.relationship_type === 'parent_child' ? 'Parent/Child' : link.relationship_type === 'milestone' ? 'Milestone' : link.relationship_type;
            ctx.strokeText(label, midX, midY);
            ctx.fillText(label, midX, midY);
          }
        }}
        enableZoomInteraction={true}
        enablePanInteraction={true}
        minZoom={0.1}
        maxZoom={3}
      />
      
      <div className="goal-map-graph-zoom-controls">
        <button
          className="goal-map-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoom(graphRef.current.zoom() * 1.2, 400);
            }
          }}
          title="Zoom In"
        >
          +
        </button>
        <button
          className="goal-map-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoom(graphRef.current.zoom() / 1.2, 400);
            }
          }}
          title="Zoom Out"
        >
          −
        </button>
        <button
          className="goal-map-graph-zoom-btn"
          onClick={() => {
            if (graphRef.current) {
              graphRef.current.zoomToFit(400, 50);
            }
          }}
          title="Reset Zoom / Fit to Screen"
        >
          ⌂
        </button>
      </div>
    </div>
  );
};

export default GoalMapGraph;

